interface InterswitchPayOptions {
  checkoutUrl: string;

  amountInNaira: string | number;
  customerEmail?: string;
  customerName?: string;
  /** Called with (txnRef, payRef) on successful payment.
   *  txnRef = the reference your backend created (use this for /payments/verify).
   *  payRef = Interswitch's internal acquirer reference (e.g. "FBN|WEB|VNA|..."), optional. */
  onSuccess: (txnRef: string, payRef?: string) => void;

  onError?: () => void;
}

declare global {
  interface Window {
    webpayCheckout: (options: Record<string, unknown>) => void;
  }
}

const QA_SCRIPT = "https://newwebpay.qa.interswitchng.com/inline-checkout.js";
const PROD_SCRIPT = "https://newwebpay.interswitchng.com/inline-checkout.js";

/** Detect which environment the checkout URL belongs to */
function getScriptSrc(checkoutUrl: string): string {
  return checkoutUrl.includes("qa.interswitchng.com") ? QA_SCRIPT : PROD_SCRIPT;
}

/** Dynamically inject the Interswitch inline script if not already loaded */
function loadInterswitchScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window.webpayCheckout === "function") {
      resolve();
      return;
    }
    // Script tag already in DOM — wait for it
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Interswitch script failed to load")),
      );
      return;
    }
    // Inject fresh script
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Interswitch script: " + src));
    document.body.appendChild(script);
  });
}

export function useInterswitchPay() {
  const pay = async ({
    checkoutUrl,
    amountInNaira,
    customerEmail = "",
    customerName = "",
    onSuccess,
    onError,
  }: InterswitchPayOptions) => {
    const scriptSrc = getScriptSrc(checkoutUrl);

    try {
      await loadInterswitchScript(scriptSrc);
    } catch (e) {
      console.error("[useInterswitchPay] Could not load Interswitch SDK:", e);
      onError?.();
      return;
    }

    if (typeof window.webpayCheckout !== "function") {
      console.error(
        "[useInterswitchPay] window.webpayCheckout is not a function after script load.",
      );
      onError?.();
      return;
    }

    // Parse the checkout URL for payment parameters
    let params: URLSearchParams;
    try {
      params = new URL(checkoutUrl).searchParams;
    } catch {
      params = new URLSearchParams(
        checkoutUrl.includes("?") ? checkoutUrl.split("?")[1] : "",
      );
    }

    const merchantCode = params.get("merchant_code") || "";
    const payItemId = params.get("pay_item_id") || "";

    // The backend uses 'reference' (and 'order_id') as the transaction ref
    const txnRef =
      params.get("reference") ||
      params.get("order_id") ||
      params.get("ref") ||
      params.get("txn_ref") ||
      "";

    // Convert Naira → kobo (Interswitch amount must be in smallest currency unit)
    const amountKobo = Math.round(Number(amountInNaira) * 100).toString();

    const siteRedirectUrl = `${window.location.origin}/individual/search-result?reference=${encodeURIComponent(txnRef)}`;

    window.webpayCheckout({
      merchant_code: merchantCode,
      pay_item_id: payItemId,
      txn_ref: txnRef,
      amount: amountKobo,
      currency: "566", // 566 = NGN
      site_redirect_url: siteRedirectUrl,
      cust_id: customerEmail,
      cust_name: customerName,
      cust_email: customerEmail,
      // Fires after every payment attempt (success or failure)
      onComplete: function (response: Record<string, string>) {
        const isApproved =
          response?.ResponseCode === "00" || response?.resp === "00";

        if (isApproved) {
          const backendRef =
            response?.MerchantReference || response?.txnRef || txnRef;
          const interswitchRef = response?.PaymentReference || response?.payRef;
          onSuccess(backendRef, interswitchRef);
        } else {
          console.warn("[useInterswitchPay] Payment not approved:", response);
          onError?.();
        }
      },
      onError: function (response: Record<string, string>) {
        console.error("[useInterswitchPay] Interswitch error:", response);
        onError?.();
      },
    });
  };

  return { pay };
}
